import { Box, BoxProps, Container } from '@chakra-ui/react';
import { useInView } from 'framer-motion';
import dynamic from 'next/dynamic';
import { rem } from 'polished';
import { useRef } from 'react';
import { useUITranslation } from '../../context/TranslationProvider';

import { NewsletterSubscriptionContent as Content } from './Content';
import { NewsletterSubscriptionDecorationProps } from './Decoration';
import { NewsletterSubscriptionForm as Form } from './Form';
import { useNewsletterSubscriptionState } from './NewsletterSubscription.state';

// There’s some DOM-stuff in NewsletterSubscriptionDecoration, so only
// render it clientside and import it dynamically to avoid NextJS errors:
const Decoration = dynamic<NewsletterSubscriptionDecorationProps>(
  () => import('./Decoration').then(module => module.NewsletterSubscriptionDecoration),
  { ssr: false },
);

const BLUR = 2; // pixels of blur when fading content out/in

export const NewsletterSubscription = (props: BoxProps) => {
  const { t } = useUITranslation();
  const ref = useRef(null);
  const isInView = useInView(ref);
  const { email } = useNewsletterSubscriptionState();

  return (
    <Container {...props}>
      <Box
        id="newsletter"
        ref={ref}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        position="relative"
        px={{ base: 'space.16', md: 'space.64' }}
        py={{ base: 'space.40', md: 'space.64' }}
        pr={{ md: '58%' }}
        minHeight={{ md: rem(366) }}
        boxSizing="border-box"
        borderRadius="radius.12"
        bg="gray.1"
      >
        <Decoration
          position={{ base: 'relative', md: 'absolute' }}
          top={{ md: '50%' }}
          right={{ md: 0 }}
          width={{ md: '50%' }}
          height={{ base: rem(165), md: rem(233) }}
          transform={{ md: 'translateY(-50%)' }}
          mr={{ base: '-space.16', md: 0 }}
          animate={isInView}
          mb={{ base: 'space.40', md: 0 }}
        />

        <Content
          title={t('common.newsletter.success.title')}
          body={t('common.newsletter.success.body', { email })}
          position="absolute"
          width="full"
          boxSizing="border-box"
          left={0}
          top={{ md: '50%' }}
          transform={{ base: 'translateY(100%)', md: 'translateY(-50%)' }}
          paddingX="inherit"
          aria-hidden={!email}
          pointerEvents={email ? undefined : 'none'}
          opacity={+!!email}
          blur={email ? 0 : BLUR}
          transitionProperty="opacity filter"
          transitionDuration="390ms"
          transitionDelay="351ms"
          transitionTimingFunction="linear"
        />

        <Content
          title={t('common.newsletter.title')}
          body={t('common.newsletter.body')}
          aria-hidden={!!email}
          pointerEvents={email ? 'none' : undefined}
          opacity={+!email}
          blur={email ? 0 : BLUR}
          transitionProperty="opacity filter"
          transitionDuration="320ms"
          transitionTimingFunction="linear"
        >
          <Form mt={{ base: 'space.28', md: 'space.20' }} />
        </Content>
      </Box>
    </Container>
  );
};
